import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  buildWizardStepDependencyMeta,
  persistWizardStepData,
  DEFAULT_MAX_RESULT_COUNT,
  DEFAULT_TOASTER_LIFE,
  IWizardContext,
  PERIOD_TYPE_NAME_SILVICULTURE,
  REASON_GROUP_NAME_AREA_ADJUSTMENT,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import { firstValueFrom, type Observable } from 'rxjs';
import type { StandDivisionAndFusionAreaMaintenanceValidationInputDto } from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/stand-division-and-fusion/models';
import { StandCatalogs } from '../../stand/stand-creation-form/services/stand-catalogs';
import { IDividedStandRow } from '../interfaces/IDividedStandRow';
import { StandDivisionAndFusion } from '../services/stand-division-and-fusion';
import { extractBackendErrorMessage, isHandledByZnErrorHandler } from '../utils/backend-error';

interface StepSelectStandsToDivideData {
  selectedStands: IDividedStandRow[];
  totalSectionedArea: number;
}

interface AreaMaintenanceFormValue {
  period: string | null;
  reason: string | null;
  adjustmentType: string | null;
  details: string;
  isAreaUpdated: boolean;
}

export interface StepDefineAreaMaintenanceData {
  sourceStep2Snapshot: string;
  selectedStands: IDividedStandRow[];
  totalSectionedArea: number;
  maintenance: AreaMaintenanceFormValue;
}

@Component({
  selector: 'lib-step-define-area-maintenance',
  standalone: true,
  imports: [LocalizationPipe, ReactiveFormsModule, ZnSingleSelect],
  templateUrl: './step-define-area-maintenance.html',
})
export class StepDefineAreaMaintenance
  implements WizardStepComponent<StepDefineAreaMaintenanceData>
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly toaster = inject(ToasterService);
  private readonly standCatalogs = inject(StandCatalogs);
  private readonly standDivisionAndFusionService = inject(StandDivisionAndFusion);

  readonly localizationKeyPrefix = 'Geographic::StandDivisionAndFusion.StepDefineAreaMaintenance';
  readonly localizationResource = ZendraLocalizationServices.LanguageService;
  readonly previousStepId = 2;

  readonly selectedStands = signal<IDividedStandRow[]>([]);
  readonly totalSectionedArea = signal(0);

  readonly form = this.formBuilder.group({
    period: [null as string | null, [Validators.required]],
    reason: [null as string | null, [Validators.required]],
    adjustmentType: [null as string | null, [Validators.required]],
    details: [''],
    isAreaUpdated: [{ value: true, disabled: true }, [Validators.requiredTrue]],
  });
  readonly controls = this.form.controls;

  readonly loadPeriod = (params?: ZnSelectQueryParams) =>
    this.loadCatalog(
      params,
      normalizedParams =>
        this.standCatalogs.loadPeriod(normalizedParams, PERIOD_TYPE_NAME_SILVICULTURE),
    );
  readonly loadReason = (params?: ZnSelectQueryParams) =>
    this.loadCatalog(
      params,
      normalizedParams =>
        this.standCatalogs.loadReason(normalizedParams, REASON_GROUP_NAME_AREA_ADJUSTMENT),
    );
  readonly loadAdjustmentTypes = (params?: ZnSelectQueryParams) =>
    this.standCatalogs.loadAdjustmentTypes(params);

  onActivate(context: IWizardContext): void {
    const activeStepId = context.activeStepId();
    const previousStepData = context.getStepState<StepSelectStandsToDivideData>(this.previousStepId)?.data;
    const currentState = context.getStepState<StepDefineAreaMaintenanceData>(activeStepId)?.data;
    const sourceStep2Snapshot = this.buildStep2Snapshot(previousStepData);
    const shouldRestoreCurrentData =
      !!currentState && currentState.sourceStep2Snapshot === sourceStep2Snapshot;

    context.patchStepState(activeStepId, {
      visited: true,
    });

    this.selectedStands.set(
      previousStepData?.selectedStands ?? (shouldRestoreCurrentData ? currentState?.selectedStands ?? [] : []),
    );
    this.totalSectionedArea.set(
      previousStepData?.totalSectionedArea ??
        (shouldRestoreCurrentData ? currentState?.totalSectionedArea ?? 0 : 0),
    );

    if (!shouldRestoreCurrentData && currentState) {
      context.patchStepState(activeStepId, {
        valid: false,
        completed: false,
        data: undefined,
        summary: [],
      });
    }

    this.form.reset(this.getInitialFormValue(shouldRestoreCurrentData ? currentState : undefined), {
      emitEvent: false,
    });
  }

  async canLeave(): Promise<boolean> {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      this.notifyError('RequiredFields');
      return false;
    }

    const input = this.buildValidationInput();
    if (!input) {
      this.notifyError('RequiredFields');
      return false;
    }

    try {
      await firstValueFrom(this.standDivisionAndFusionService.validateAreaMaintenance(input));
      return true;
    } catch (error) {
      this.handleValidationError(error);
      return false;
    }
  }

  canLeaveSync(): boolean {
    return true;
  }

  collect(context: IWizardContext): StepDefineAreaMaintenanceData {
    const data: StepDefineAreaMaintenanceData = {
      sourceStep2Snapshot: this.buildStep2Snapshot({
        selectedStands: this.selectedStands(),
        totalSectionedArea: this.totalSectionedArea(),
      }),
      selectedStands: this.selectedStands(),
      totalSectionedArea: this.totalSectionedArea(),
      maintenance: this.toAreaMaintenanceFormValue(),
    };

    persistWizardStepData(context, {
      visited: true,
      valid: this.form.valid,
      completed: true,
      data,
      dirty: true,
      meta: buildWizardStepDependencyMeta(data),
      invalidateStepIds: [4, 5],
    });

    return data;
  }

  fieldInvalid(field: keyof AreaMaintenanceFormValue): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  private getInitialFormValue(
    currentState?: StepDefineAreaMaintenanceData,
  ): AreaMaintenanceFormValue {
    const maintenance = currentState?.maintenance;

    return {
      period: maintenance?.period ?? null,
      reason: maintenance?.reason ?? null,
      adjustmentType: maintenance?.adjustmentType ?? null,
      details: maintenance?.details ?? '',
      isAreaUpdated: maintenance?.isAreaUpdated ?? true,
    };
  }

  private loadCatalog<T>(
    params: ZnSelectQueryParams | undefined,
    loader: (normalizedParams: ZnSelectQueryParams) => Observable<T>,
  ): Observable<T> {
    return loader({
      ...params,
      maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
    });
  }

  private toAreaMaintenanceFormValue(): AreaMaintenanceFormValue {
    const rawValue = this.form.getRawValue();

    return {
      period: rawValue.period ?? null,
      reason: rawValue.reason ?? null,
      adjustmentType: rawValue.adjustmentType ?? null,
      details: rawValue.details?.trim() ?? '',
      isAreaUpdated: !!rawValue.isAreaUpdated,
    };
  }

  private buildValidationInput(): StandDivisionAndFusionAreaMaintenanceValidationInputDto | null {
    const rawValue = this.form.getRawValue();
    const periodId = this.toPositiveInteger(rawValue.period);
    const adjustmentReasonId = this.toPositiveInteger(rawValue.reason);
    const standAreaMaintenanceTypeId = this.toPositiveInteger(rawValue.adjustmentType);

    if (!periodId || !adjustmentReasonId || !standAreaMaintenanceTypeId) {
      return null;
    }

    return {
      periodId,
      adjustmentReasonId,
      standAreaMaintenanceTypeId,
      selectedStandIds: this.selectedStands()
        .map(stand => this.toPositiveInteger(stand.id))
        .filter((standId): standId is number => !!standId),
    };
  }

  private buildStep2Snapshot(value?: StepSelectStandsToDivideData): string {
    return JSON.stringify({
      selectedStands: (value?.selectedStands ?? []).map(stand => ({
        id: Number(stand.id),
        currentArea: this.roundArea(stand.currentArea),
        newArea: this.roundArea(stand.newArea),
        sectionedArea: this.roundArea(stand.sectionedArea),
      })),
      totalSectionedArea: this.roundArea(value?.totalSectionedArea ?? 0),
    });
  }

  private handleValidationError(error: unknown): void {
    if (isHandledByZnErrorHandler(error)) {
      return;
    }

    const message = extractBackendErrorMessage(error);
    if (!message) {
      return;
    }

    this.toaster.error(message, '', { life: DEFAULT_TOASTER_LIFE });
  }


  private notifyError(key: string): void {
    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandDivisionAndFusion.StepDefineAreaMaintenance.${key}`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
  }

  private toPositiveInteger(value: number | string | null | undefined): number | null {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  private roundArea(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }


}

